/* @flow */

import * as React from 'react';

import type {
  NETGEM_API_V8_METADATA_LOCATION_VIDEO_STREAM_PARAM,
  NETGEM_API_V8_METADATA_SCHEDULE,
  NETGEM_API_V8_METADATA_SCHEDULE_LOCATION,
} from '../../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { BO_API_AUDIO_AND_SUBS_TYPE, BO_API_LANGUAGE_TYPE } from '../../../libs/netgemLibrary/videofutur/types/Language';

import { PictoHearingImpaired, PictoVisuallyImpaired } from '../../../components/pictos/Element';
import { getLocationType } from '../../../libs/netgemLibrary/v8/helpers/Item';
import getTranslatedText from '../../../libs/netgemLibrary/v8/helpers/Lang';
import { ItemLocationKind, type NETGEM_API_V8_FEED_ITEM } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import { type NETGEM_API_V8_LANG, SynopsisType } from '../../../libs/netgemLibrary/v8/types/Lang';
import { METADATA_KIND_PROGRAM, type NETGEM_API_V8_METADATA, type NETGEM_API_V8_METADATA_PROGRAM, type NETGEM_API_V8_METADATA_SERIES } from '../../../libs/netgemLibrary/v8/types/MetadataProgram';
import { ISO8601_DURATION_ZERO, formatIso8601Duration, getIso8601DateInSeconds, getIso8601DurationInSeconds } from '../../dateTime/Format';
import Localizer from '../../localization';
import ParentalControlManager from '../../parentalControl/parentalControlManager';
import { LANGUAGE, PROGRAM_INFO } from './Types';

const addIfNotNull =
  <T>(elements: Array<T>): ((element: T | null) => void) =>
  (element: T | null) => {
    if (element !== null) {
      elements.push(element);
    }
  };

const buildTitle: (language: string, programMetadata: NETGEM_API_V8_METADATA_PROGRAM | null, seriesMetadata: NETGEM_API_V8_METADATA_SERIES | null, alwaysDisplaySeriesTitle: boolean) => string = (
  language,
  programMetadata,
  seriesMetadata,
  alwaysDisplaySeriesTitle,
) => {
  const titleArray: Array<string> = [];
  const addToTitleArrayIfNotNull = addIfNotNull<string>(titleArray);

  let seriesTitle: string | null = null;
  if (seriesMetadata) {
    const { titles } = seriesMetadata;
    if (titles.length > 0) {
      seriesTitle = getTranslatedText(titles, language);
    }
  }

  if (programMetadata) {
    const { episodeIndex, episodeTitle } = getEpisodeIndexAndTitle(programMetadata, seriesTitle, language);
    addToTitleArrayIfNotNull(episodeIndex);
    addToTitleArrayIfNotNull(episodeTitle);
  }

  if ((alwaysDisplaySeriesTitle || titleArray.length === 0) && seriesTitle) {
    titleArray.unshift(seriesTitle);
  }

  return titleArray.length > 0 ? titleArray.join(' - ') : Localizer.localize('common.metadata.unknown_title');
};

const renderProgramLanguage: (locationMetadata: NETGEM_API_V8_METADATA_SCHEDULE_LOCATION | null) => React.Node = (locationMetadata) => {
  const { languageElements, languagesAriaLabel } = formatAudioAndSubtitles(locationMetadata);

  if (!languageElements) {
    return null;
  }

  return (
    <div className='languages' key='languages'>
      <span className='screenReaderOnly'>{Localizer.localize('accessibility.content.available_languages', { languages: languagesAriaLabel })}</span>
      {languageElements}
    </div>
  );
};

const renderProgramParentalGuidance: (item: NETGEM_API_V8_FEED_ITEM) => React.Node = (item) => ParentalControlManager.getLabelElementFromItem(item);

const renderProgramDuration: (item: NETGEM_API_V8_FEED_ITEM, metadata: NETGEM_API_V8_METADATA_PROGRAM) => React.Node = (item, metadata) => {
  const {
    locType,
    selectedLocation: { duration: locationDuration, quotaUsed, scheduledEventDuration },
  } = item;
  const { duration: programDuration } = metadata;
  const localDuration = scheduledEventDuration ?? locationDuration ?? programDuration;

  if (localDuration && localDuration !== ISO8601_DURATION_ZERO) {
    const formattedDuration = formatIso8601Duration(localDuration);
    if (formattedDuration !== '') {
      const wasNotFullyRecorded = locType === ItemLocationKind.Recording && typeof quotaUsed === 'string' && getIso8601DurationInSeconds(quotaUsed) < getIso8601DurationInSeconds(localDuration);

      const recordedDuration = wasNotFullyRecorded ? (
        <>
          <span className='real'>{formatIso8601Duration(quotaUsed)}</span>
          <span className='separator'>/</span>
        </>
      ) : null;

      return (
        <div className='duration' key='duration'>
          {recordedDuration}
          {formattedDuration}
        </div>
      );
    }
  }

  return null;
};

const renderPlayerCount: (maxPlayers: number) => React.Node = (maxPlayers) => {
  const text = maxPlayers === 1 ? Localizer.localize('gaming.player_count.player_one') : Localizer.localize('gaming.player_count.player_other', { count: maxPlayers });

  return <div key='players'>{text}</div>;
};

const renderProgramDetails: (item: NETGEM_API_V8_FEED_ITEM, metadata: ?NETGEM_API_V8_METADATA, vodLocations: ?Array<NETGEM_API_V8_METADATA_SCHEDULE>, info: number) => Array<React.Node> | null = (
  item,
  metadata,
  vodLocations,
  info,
) => {
  if (!metadata) {
    return null;
  }

  const { gameMetadata, genres, productionCountry, releaseDate } = metadata;
  const genreArray = genres && genres.values && genres.values.length > 0 ? genres.values : null;
  const details: Array<React.Node> = [];
  const addToDetailsIfNotNull = addIfNotNull<React.Node>(details);

  if (info & PROGRAM_INFO.Duration && metadata.type === METADATA_KIND_PROGRAM) {
    addToDetailsIfNotNull(renderProgramDuration(item, metadata as NETGEM_API_V8_METADATA_PROGRAM));
  }

  if (info & PROGRAM_INFO.Genre && genreArray) {
    genreArray.forEach((genre) =>
      details.push(
        <div className='genre' key={`genre-${genre}`}>
          {genre}
        </div>,
      ),
    );
  }

  if (info & PROGRAM_INFO.ProductionCountry && productionCountry) {
    details.push(
      <div className='country' key='country'>
        <span className='screenReaderOnly'>{Localizer.localize('accessibility.content.production_country', { country: productionCountry })}</span>
        <span aria-hidden>{productionCountry}</span>
      </div>,
    );
  }

  if (info & PROGRAM_INFO.ReleaseDate && releaseDate) {
    details.push(
      <div className='date release' key='releaseDate'>
        <span className='screenReaderOnly'>{Localizer.localize('accessibility.content.production_date', { date: releaseDate })}</span>
        <span aria-hidden>{releaseDate}</span>
      </div>,
    );
  }

  if (info & PROGRAM_INFO.Language && vodLocations && vodLocations.length > 0) {
    addToDetailsIfNotNull(renderProgramLanguage(vodLocations[0].location));
  }

  if (info & PROGRAM_INFO.ParentalGuidance && metadata.type === METADATA_KIND_PROGRAM) {
    addToDetailsIfNotNull(renderProgramParentalGuidance(item));
  }

  if (gameMetadata) {
    const { localMultiplayerCount } = gameMetadata;
    details.push(renderPlayerCount(localMultiplayerCount));
  }

  if (details.length === 0) {
    // Nothing to render
    return null;
  }

  // React transforms the array into elements
  return details;
};

/*
 * Format Season Episode number in SXX EXX
 * ex: => "S0 E1"
 * ex: => "S1 E10"
 * ex: => "S1"
 * ex: => "E2"
 *
 * @public
 * @param  {NETGEM_API_V8_METADATA_PROGRAM} metadata: item metadata
 * @return {string}                                 : formatted result
 */
const formatSeasonEpisodeNbr: (programMetadata: NETGEM_API_V8_METADATA_PROGRAM | null) => string | null = (programMetadata) => {
  if (!programMetadata) {
    return null;
  }

  let textResult: string | null = null;

  const { episodeOf: { episodeNumber = 0, seasonNumber = 0 } = {} } = programMetadata;
  if (seasonNumber > 0) {
    textResult = `S${seasonNumber}`;
  }
  if (episodeNumber > 0) {
    textResult = `${textResult ? `${textResult} E` : ''}${episodeNumber}`;
  }

  return textResult;
};

const hasLanguage: (languages: BO_API_AUDIO_AND_SUBS_TYPE, language: string) => boolean = (languages, language) => languages.some((l) => l.language.toUpperCase() === language) ?? false;

const hasNotVFLanguage: (languages: BO_API_AUDIO_AND_SUBS_TYPE) => boolean = (languages) => languages.some((l) => l.language.toUpperCase() !== LANGUAGE.FR) ?? false;

const isEpisodeIndexInTitle: (title: string) => boolean = (title) => /S\d{1,3} ?E\d{1,5}/iu.test(title);

const isVisuallyImpairedAudio = (track: BO_API_LANGUAGE_TYPE): boolean => {
  const { audioDescription, language } = track;

  if (audioDescription) {
    return true;
  }

  const ucLanguage = language.toUpperCase();
  return ucLanguage === LANGUAGE.VisuallyImpaired || ucLanguage === LANGUAGE.VisuallyImpairedAlt;
};

const formatAudioAndSubtitles: (locationMetadata: ?NETGEM_API_V8_METADATA_SCHEDULE_LOCATION) => { languageElements: React.Node, languagesAriaLabel: string } = (locationMetadata) => {
  const defaultResult: { languageElements: React.Node, languagesAriaLabel: string } = { languageElements: null, languagesAriaLabel: '' };

  if (!locationMetadata) {
    return defaultResult;
  }

  const { playbackUrls } = locationMetadata;

  if (!playbackUrls || playbackUrls.length === 0) {
    return defaultResult;
  }

  const [
    {
      start: { params },
    },
  ] = playbackUrls;

  const videostreams = params?.find((i) => i.name === 'videostreams');
  const propertiesArg = videostreams?.value.args.find((i) => (i as Object as NETGEM_API_V8_METADATA_LOCATION_VIDEO_STREAM_PARAM).properties);

  if (!propertiesArg) {
    return defaultResult;
  }

  const { properties } = propertiesArg as Object as NETGEM_API_V8_METADATA_LOCATION_VIDEO_STREAM_PARAM;
  if (!properties) {
    return defaultResult;
  }

  const { audio, subtitles } = properties;

  if (!audio && !subtitles) {
    return defaultResult;
  }

  /*
   * VOD case:
   *  - FR only                             : VF
   *  - FR + another language               : VF VOST
   *  - FR + another language + FR subtitles: VF VOST
   *  - Another language + FR subtitles     : VOST
   *  - Another language                    : VOST
   *
   * TV case:
   *  - FR only                             : VF
   *  - FR + QAA                            : VF VOST
   *  - FR + QAA + FR subtitles             : VF VOST
   *  - QAA + FR subtitles                  : VOST
   *  - QAA                                 : VOST
   */

  const audioAndSubs: Array<string> = [];
  const audioAndSubsAriaLabel: Array<string> = [];
  let visuallyImpairedElt: React.Node = null;
  let hearingImpairedElt: React.Node = null;

  if (audio) {
    if (hasLanguage(audio, LANGUAGE.FR)) {
      audioAndSubs.push(Localizer.localize('common.metadata.language.french'));
      audioAndSubsAriaLabel.push(Localizer.localize('accessibility.content.track_french'));
    }

    if (hasNotVFLanguage(audio) || hasLanguage(audio, LANGUAGE.OriginalVersion)) {
      audioAndSubs.push(Localizer.localize('common.metadata.language.original_version_with_subtitles'));
      audioAndSubsAriaLabel.push(Localizer.localize('accessibility.content.track_subtitled'));
    }

    if (audio.some((track) => isVisuallyImpairedAudio(track))) {
      visuallyImpairedElt = <PictoVisuallyImpaired aria-hidden className='pictoInfo impaired' key='visuallyImpaired' />;
      audioAndSubsAriaLabel.push(Localizer.localize('accessibility.content.track_visually_impaired'));
    }
  }

  if (subtitles && subtitles.some((sub) => sub.captions === true)) {
    hearingImpairedElt = <PictoHearingImpaired aria-hidden className='pictoInfo impaired' key='hearingImpaired' />;
    audioAndSubsAriaLabel.push(Localizer.localize('accessibility.content.track_hearing_impaired'));
  }

  if (audioAndSubs.length === 0) {
    return defaultResult;
  }

  const languageElements = (
    <>
      {audioAndSubs.map((lang) => (
        <div aria-hidden className='pictoInfo' key={lang}>
          {lang}
        </div>
      ))}
      {visuallyImpairedElt}
      {hearingImpairedElt}
    </>
  );

  const languagesAriaLabel = audioAndSubsAriaLabel.join(', ');

  return { languageElements, languagesAriaLabel };
};

const getEpisodeIndexAndTitle: (programMetadata: ?NETGEM_API_V8_METADATA_PROGRAM, seriesTitle: ?string, language: string) => { episodeIndex: string | null, episodeTitle: string | null } = (
  programMetadata,
  seriesTitle,
  language,
) => {
  if (!programMetadata) {
    return {
      episodeIndex: null,
      episodeTitle: null,
    };
  }

  const { titles } = programMetadata;
  const title = getTranslatedText(titles, language);
  const episodeIndex = formatSeasonEpisodeNbr(programMetadata);
  const episodeTitle = title && (!seriesTitle || title.toLowerCase() !== seriesTitle.toLowerCase()) ? title : null;

  return {
    episodeIndex,
    episodeTitle,
  };
};

const getLocalizedString: (langArray: Array<NETGEM_API_V8_LANG>, language: string, fallbackValue: ?string, requestedType?: SynopsisType) => string | null = (
  langArray,
  language,
  fallbackValue,
  requestedType,
) => {
  const baseLang = (language.includes('-') ? language.substring(0, language.indexOf('-')) : language).toUpperCase();
  let defaultText: string | null = null;

  for (const { lang, type, value } of langArray) {
    if (lang.toUpperCase() === baseLang) {
      // For synopsis and tagline, look for correct type, but for title, first value is OK
      if (requestedType === undefined || type === requestedType) {
        return value;
      }

      if (requestedType !== SynopsisType.Short) {
        // For full synopsis (resp. title), another kind of synopsis (resp. title) could be used in case nothing perfect is found
        defaultText = value;
      }
    }
  }

  if (!defaultText) {
    // Find available correct synopsis kind in any language
    for (const { type, value } of langArray) {
      if (!requestedType || type === requestedType) {
        defaultText = value;
        break;
      }
    }
  }

  // At this point, defaultText is either null (nothing was found for the given language) or has a value for the wrong type but in the right language
  return defaultText ?? fallbackValue ?? null;
};

const getTitleInternal: (metadata: ?NETGEM_API_V8_METADATA, language: string, fallbackValue: ?string) => string | null = (metadata, language, fallbackValue) => {
  if (!metadata) {
    return null;
  }

  const { titles } = metadata;

  if (!titles) {
    return null;
  }

  return getLocalizedString(titles, language, fallbackValue);
};

const getSynopsisInternal: (metadata: ?NETGEM_API_V8_METADATA, language: string, fallbackValue: ?string, requestedType: SynopsisType) => string | null = (
  metadata,
  language,
  fallbackValue,
  requestedType,
) => {
  if (!metadata) {
    return null;
  }

  const { synopses } = metadata;

  if (!synopses) {
    return null;
  }

  return getLocalizedString(synopses, language, fallbackValue, requestedType);
};

const getTagline: (metadata: ?NETGEM_API_V8_METADATA, language: string, fallbackValue: ?string) => string | null = (metadata, language, fallbackValue = null) =>
  getSynopsisInternal(metadata, language, fallbackValue, SynopsisType.Short);

const getSynopsis: (metadata: ?NETGEM_API_V8_METADATA, language: string, fallbackValue: ?string) => string | null = (metadata, language, fallbackValue = null) =>
  getSynopsisInternal(metadata, language, fallbackValue, SynopsisType.Full);

const getTitle: (metadata: ?NETGEM_API_V8_METADATA, language: string, fallbackValue: ?string) => string | null = (metadata, language, fallbackValue = null) =>
  getTitleInternal(metadata, language, fallbackValue);

const getStartAndEndTimes: (item: NETGEM_API_V8_FEED_ITEM) => { endTime: number, startTime: number } = (item) => {
  const {
    selectedLocation: { id, scheduledEventDuration, scheduledEventStartDate },
  } = item;
  let { endTime: localEndTime, startTime: localStartTime } = item;

  const locType = getLocationType(id);

  if ((locType === ItemLocationKind.Catchup || locType === ItemLocationKind.Recording) && scheduledEventDuration && scheduledEventStartDate) {
    localStartTime = getIso8601DateInSeconds(scheduledEventStartDate);
    localEndTime = localStartTime + getIso8601DurationInSeconds(scheduledEventDuration);
  }

  return {
    endTime: localEndTime,
    startTime: localStartTime,
  };
};

export {
  buildTitle,
  formatAudioAndSubtitles,
  formatSeasonEpisodeNbr,
  getEpisodeIndexAndTitle,
  getLocalizedString,
  getStartAndEndTimes,
  getSynopsis,
  getTagline,
  getTitle,
  isEpisodeIndexInTitle,
  renderProgramDetails,
  renderProgramLanguage,
  renderProgramParentalGuidance,
};
